// Pure Web Audio API Synthesizer for Captain PopX
// 100% Procedural & Original - Zero external audio assets, zero copyright risks

class AudioSynth {
  constructor () {
    this.ctx = null
    this.muted = false

    try {
      const storedMute = localStorage.getItem('captain_popx_muted')
      if (storedMute !== null) {
        this.muted = storedMute === 'true'
      }
    } catch (e) {
      this.muted = false
    }
  }

  init () {
    if (!this.ctx && typeof window !== 'undefined') {
      const AudioCtx = window.AudioContext || window.webkitAudioContext
      if (AudioCtx) {
        this.ctx = new AudioCtx()
      }
    }
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume()
    }
  }

  toggleMute () {
    this.muted = !this.muted
    try {
      localStorage.setItem('captain_popx_muted', this.muted ? 'true' : 'false')
    } catch (e) {}
    return this.muted
  }

  isMuted () {
    return this.muted
  }

  playLaser () {
    if (this.muted) return
    this.init()
    if (!this.ctx) return

    try {
      const osc = this.ctx.createOscillator()
      const gain = this.ctx.createGain()

      osc.type = 'sawtooth'
      const now = this.ctx.currentTime
      osc.frequency.setValueAtTime(880, now)
      osc.frequency.exponentialRampToValueAtTime(120, now + 0.12)

      gain.gain.setValueAtTime(0.15, now)
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.12)

      osc.connect(gain)
      gain.connect(this.ctx.destination)

      osc.start(now)
      osc.stop(now + 0.12)
    } catch (e) {}
  }

  playExplosion () {
    if (this.muted) return
    this.init()
    if (!this.ctx) return

    try {
      // Reusable noise burst buffer for explosion
      if (!this.noiseBuffer) {
        const bufferSize = Math.floor(this.ctx.sampleRate * 0.25)
        this.noiseBuffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate)
        const output = this.noiseBuffer.getChannelData(0)
        for (let i = 0; i < bufferSize; i++) {
          output[i] = Math.random() * 2 - 1
        }
      }

      const whiteNoise = this.ctx.createBufferSource()
      whiteNoise.buffer = this.noiseBuffer

      const filter = this.ctx.createBiquadFilter()
      filter.type = 'lowpass'
      const now = this.ctx.currentTime
      filter.frequency.setValueAtTime(800, now)
      filter.frequency.exponentialRampToValueAtTime(50, now + 0.25)

      const gain = this.ctx.createGain()
      gain.gain.setValueAtTime(0.3, now)
      gain.gain.exponentialRampToValueAtTime(0.01, now + 0.25)

      whiteNoise.connect(filter)
      filter.connect(gain)
      gain.connect(this.ctx.destination)

      whiteNoise.start(now)
      whiteNoise.stop(now + 0.25)
    } catch (e) {}
  }

  playHit () {
    if (this.muted) return
    this.init()
    if (!this.ctx) return

    try {
      const osc = this.ctx.createOscillator()
      const gain = this.ctx.createGain()
      const now = this.ctx.currentTime

      osc.type = 'triangle'
      osc.frequency.setValueAtTime(220, now)
      osc.frequency.exponentialRampToValueAtTime(80, now + 0.08)

      gain.gain.setValueAtTime(0.2, now)
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08)

      osc.connect(gain)
      gain.connect(this.ctx.destination)

      osc.start(now)
      osc.stop(now + 0.08)
    } catch (e) {}
  }

  playPowerup () {
    if (this.muted) return
    this.init()
    if (!this.ctx) return

    try {
      const now = this.ctx.currentTime
      const notes = [330, 440, 550, 660, 880]
      notes.forEach((freq, i) => {
        const osc = this.ctx.createOscillator()
        const gain = this.ctx.createGain()
        osc.type = 'sine'
        osc.frequency.setValueAtTime(freq, now + i * 0.05)
        gain.gain.setValueAtTime(0.12, now + i * 0.05)
        gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.05 + 0.1)

        osc.connect(gain)
        gain.connect(this.ctx.destination)
        osc.start(now + i * 0.05)
        osc.stop(now + i * 0.05 + 0.1)
      })
    } catch (e) {}
  }

  playGameOver () {
    if (this.muted) return
    this.init()
    if (!this.ctx) return

    try {
      const now = this.ctx.currentTime
      const notes = [320, 280, 240, 180]
      notes.forEach((freq, i) => {
        const osc = this.ctx.createOscillator()
        const gain = this.ctx.createGain()
        osc.type = 'sawtooth'
        osc.frequency.setValueAtTime(freq, now + i * 0.15)
        gain.gain.setValueAtTime(0.2, now + i * 0.15)
        gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.15 + 0.2)

        osc.connect(gain)
        gain.connect(this.ctx.destination)
        osc.start(now + i * 0.15)
        osc.stop(now + i * 0.15 + 0.2)
      })
    } catch (e) {}
  }

  playSonar () {
    if (this.muted) return
    this.init()
    if (!this.ctx) return

    try {
      const osc = this.ctx.createOscillator()
      const gain = this.ctx.createGain()
      const now = this.ctx.currentTime

      osc.type = 'sine'
      osc.frequency.setValueAtTime(1046.5, now)
      osc.frequency.exponentialRampToValueAtTime(523.25, now + 0.35)

      gain.gain.setValueAtTime(0.2, now)
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35)

      osc.connect(gain)
      gain.connect(this.ctx.destination)

      osc.start(now)
      osc.stop(now + 0.35)
    } catch (e) {}
  }

  playDiscovery () {
    if (this.muted) return
    this.init()
    if (!this.ctx) return

    try {
      const now = this.ctx.currentTime
      const notes = [523.25, 659.25, 783.99, 1046.5]
      notes.forEach((freq, i) => {
        const osc = this.ctx.createOscillator()
        const gain = this.ctx.createGain()
        osc.type = 'triangle'
        osc.frequency.setValueAtTime(freq, now + i * 0.08)
        gain.gain.setValueAtTime(0.18, now + i * 0.08)
        gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.08 + 0.25)

        osc.connect(gain)
        gain.connect(this.ctx.destination)
        osc.start(now + i * 0.08)
        osc.stop(now + i * 0.08 + 0.25)
      })
    } catch (e) {}
  }
}

const synth = new AudioSynth()
export default synth
